import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SchemaOrg from "@/components/base/SchemaOrg";
import { groupSchools, groupCompanies, relatedOrganizations } from "@/mocks/links";

const siteUrl = import.meta.env.VITE_SITE_URL;

const linksPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "中央カレッジグループ | グループ校・グループ企業一覧 | 株式会社TBM健康美容研究所",
  "description": "全国に展開する中央カレッジグループのグループ校10校、グループ企業9社、関連機関の一覧。美容・理容・医療・福祉・情報・農業分野の専門学校と企業情報を掲載。",
  "url": `${siteUrl}/links`,
  "publisher": {
    "@type": "Organization",
    "name": "株式会社TBM健康美容研究所",
    "url": siteUrl
  },
  "about": {
    "@type": "Organization",
    "name": "中央カレッジグループ",
    "url": "https://www.chuo.ac.jp/tbm/"
  }
};

export default function LinksPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg data={linksPageSchema} />
      <Header showBusinessContent={false} />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://media.genaiweb.ai/73acebca-6391-4da3-b270-472de16380f3/links-hero-corporate-07.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          <div className="relative h-full flex flex-col items-center justify-center text-center px-4 z-10 w-full">
            <span className="text-white/60 text-xs tracking-[0.3em] mb-5">
              CENTRAL COLLEGE GROUP
            </span>
            <h1 className="text-white text-3xl md:text-5xl lg:text-6xl font-bold tracking-wide mb-4">
              中央カレッジグループ
            </h1>
            <p className="text-white/70 text-sm md:text-base max-w-2xl leading-relaxed">
              全国に展開する教育機関グループ。美容・理容・医療・福祉・情報・農業など、
              多彩な分野で次世代のプロフェッショナルを育成しています。
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
              <a
                href="#schools"
                className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] text-sm font-medium px-6 py-3 rounded-full transition-all duration-300 hover:bg-[#E60073] hover:text-white whitespace-nowrap"
              >
                <i className="ri-school-line text-sm"></i>
                <span>グループ校を見る</span>
              </a>
              <a
                href="#companies"
                className="inline-flex items-center gap-2 border border-white/40 text-white text-sm font-medium px-6 py-3 rounded-full transition-all duration-300 hover:bg-white hover:text-[#1A1A1A] whitespace-nowrap"
              >
                <i className="ri-building-2-line text-sm"></i>
                <span>グループ企業を見る</span>
              </a>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="w-full bg-white py-12 md:py-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-4">
                学びの、その先へ。
              </h2>
              <p className="text-[#555555] text-sm md:text-base leading-relaxed">
                中央カレッジグループは、学校法人有坂中央学園を母体とする教育グループです。
                群馬県を拠点に、全国で専門学校・大学校を展開し、卒業生は30,000名を超えます。
                グループ企業との連携により、教育から就職、キャリア形成までをトータルでサポート。
                「社会で活躍できる人材の育成」を使命に、実践的な職業教育を提供し続けています。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10 md:mt-14">
              {[
                { value: "10", label: "グループ校" },
                { value: "9", label: "グループ企業" },
                { value: "30,000+", label: "卒業生数" },
                { value: "50+", label: "学科・コース数" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-5 md:p-6 text-center"
                >
                  <div className="text-[#E60073] text-2xl md:text-3xl font-bold mb-1">{stat.value}</div>
                  <div className="text-[#666666] text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Group Schools */}
        <section id="schools" className="w-full bg-[#F5F5F5] py-12 md:py-16 lg:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <span className="text-[#E60073] text-xs tracking-[0.2em] block mb-2">
                SCHOOL
              </span>
              <h2 className="text-[#1A1A1A] text-2xl md:text-3xl font-bold tracking-wide">
                グループ校一覧
              </h2>
              <p className="text-[#666666] text-sm mt-2">
                美容・理容から医療、情報、農業まで。多彩な分野でプロフェッショナルを育成する10の専門学校・大学校。
              </p>
              <div className="w-10 h-px bg-[#E60073] mt-4" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {groupSchools.map((school) => (
                <a
                  key={school.id}
                  href={school.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col bg-white border border-[#E5E5E5] rounded-lg p-5 md:p-6 transition-all duration-300 hover:border-[#E60073] hover:shadow-sm cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="text-[#1A1A1A] text-base font-bold group-hover:text-[#E60073] transition-colors">
                      {school.name}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-external-link-line text-[#999999] text-sm group-hover:text-[#E60073] transition-colors" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {school.courses.map((course) => (
                      <span
                        key={course}
                        className="inline-block text-[#666666] text-[10px] md:text-xs bg-[#F5F5F5] border border-[#E5E5E5] px-2 py-1 rounded-full"
                      >
                        {course}
                      </span>
                    ))}
                  </div>
                  <p className="text-[#555555] text-xs md:text-sm leading-relaxed mt-3">
                    {school.description}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Group Companies */}
        <section id="companies" className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <span className="text-[#E60073] text-xs tracking-[0.2em] block mb-2">
                COMPANY
              </span>
              <h2 className="text-[#1A1A1A] text-2xl md:text-3xl font-bold tracking-wide">
                グループ企業一覧
              </h2>
              <p className="text-[#666666] text-sm mt-2">
                教育を支え、社会とつなぐ。シンクタンクから人材、IT、農業まで幅広い事業を展開するグループ企業。
              </p>
              <div className="w-10 h-px bg-[#E60073] mt-4" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {groupCompanies.map((company) => (
                <a
                  key={company.id}
                  href={company.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-5 md:p-6 transition-all duration-300 hover:border-[#E60073] hover:shadow-sm cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-[#1A1A1A] text-base font-bold group-hover:text-[#E60073] transition-colors">
                      {company.name}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-external-link-line text-[#999999] text-sm group-hover:text-[#E60073] transition-colors" />
                    </div>
                  </div>
                  {company.tagline && (
                    <p className="text-[#666666] text-xs">{company.tagline}</p>
                  )}
                  <p className="text-[#555555] text-xs md:text-sm leading-relaxed mt-2">
                    {company.description}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Related Organizations */}
        <section className="w-full bg-[#F5F5F5] py-12 md:py-16 lg:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <span className="text-[#E60073] text-xs tracking-[0.2em] block mb-2">
                RELATED
              </span>
              <h2 className="text-[#1A1A1A] text-2xl md:text-3xl font-bold tracking-wide">
                教育力向上への取り組み
              </h2>
              <p className="text-[#666666] text-sm mt-2">
                より質の高い教育を実現するための、学外パートナーシップと関連機関。
              </p>
              <div className="w-10 h-px bg-[#E60073] mt-4" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {relatedOrganizations.map((org) => (
                <a
                  key={org.id}
                  href={org.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-3 bg-white border border-[#E5E5E5] rounded-lg px-5 py-4 transition-all duration-300 hover:border-[#E60073] hover:shadow-sm cursor-pointer"
                >
                  <span className="text-[#1A1A1A] text-sm font-medium group-hover:text-[#E60073] transition-colors">
                    {org.name}
                  </span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-external-link-line text-[#999999] text-sm group-hover:text-[#E60073] transition-colors" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full bg-white py-12 md:py-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-6 md:p-8 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#E60073]/10">
                <i className="ri-links-line text-[#E60073] text-lg"></i>
              </div>
              <h3 className="text-[#1A1A1A] text-lg font-bold mb-2">
                中央カレッジグループについて
              </h3>
              <p className="text-[#666666] text-sm leading-relaxed mb-5">
                上記のリンクはすべて中央カレッジグループ公式サイトの情報をもとに作成しています。
                各学校・企業の詳細については、リンク先の公式サイトをご確認ください。
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 text-[#E60073] text-sm font-medium hover:text-[#B8005C] transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                <span>TBM健康美容研究所 トップページに戻る</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}